import type React from "react";
import { useTranslation } from "react-i18next";
import type { BriefDetails } from "src/model/model";

const BRIEF_LABELS: Record<string, string> = {
  expectedTone: "interactionDetails.tone",
  expectedKeyPoints: "interactionDetails.keyPoints",
  expectedLabel: "interactionDetails.label",
  expectedNextSceneIntent: "interactionDetails.nextSceneIntent",
  expectedCountdownSeconds: "interactionDetails.countdownSeconds",
  expectedQuestion: "interactionDetails.question",
  expectedCorrectAnswer: "interactionDetails.correctAnswer",
  expectedAnswerOptions: "interactionDetails.answerOptions",
  expectedDifficulty: "interactionDetails.difficulty",
  expectedKeywords: "interactionDetails.keywords",
  expectedPersonaName: "interactionDetails.personaName",
  expectedPersonaTone: "interactionDetails.personaTone",
  expectedPersonaTraits: "interactionDetails.personaTraits",
  expectedKeyTopics: "interactionDetails.keyTopics",
  expectedSituationContext: "interactionDetails.situationContext",
  expectedUserRole: "interactionDetails.userRole",
  expectedSuccessCriteria: "interactionDetails.successCriteria",
  expectedFailureCriteria: "interactionDetails.failureCriteria",
  expectedHotspotSubjects: "interactionDetails.hotspotSubjects",
  expectedAssetDescription: "interactionDetails.assetDescription",
  expectedCategories: "interactionDetails.categories",
  expectedScoreCriteria: "interactionDetails.scoreCriteria",
};

interface GenericKeyValueDetailsProps {
  data: BriefDetails | Record<string, unknown>;
  mode: "plan" | "recap";
}

const GenericKeyValueDetails: React.FC<GenericKeyValueDetailsProps> = ({ data, mode }) => {
  const { t } = useTranslation();
  const ns = "pages.chat";

  if (!data || typeof data !== "object") return null;

  // Only primitives (and arrays of them) are renderable here. An object-valued
  // field has no meaningful generic rendering — String() would print
  // "[object Object]" — so it is dropped; give the type its own renderer in
  // InteractionDetails' registry when such a field must be shown.
  const isRenderable = (v: unknown): boolean =>
    v !== null && v !== undefined && typeof v !== "object" && typeof v !== "function";

  const entries = Object.entries(data).filter(([, v]) => {
    if (v === null || v === undefined) return false;
    if (Array.isArray(v)) return v.length > 0 && v.every(isRenderable);
    if (typeof v === "object") return false;
    if (typeof v === "string" && v.length === 0) return false;
    return true;
  });

  if (entries.length === 0) return null;

  return (
    <div className="space-y-1 text-[11px] text-gray-700">
      {entries.map(([key, value]) => {
        const labelKey = mode === "plan" ? BRIEF_LABELS[key] : undefined;
        const label = labelKey ? t(`${ns}.${labelKey}`) : key;
        const display = Array.isArray(value) ? value.join(", ") : String(value);
        return (
          <div key={key} className="flex gap-1.5">
            <span className="font-medium text-gray-500 shrink-0">{label} :</span>
            <span>{display}</span>
          </div>
        );
      })}
    </div>
  );
};

export default GenericKeyValueDetails;
